import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Wrench } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var serviceTypes = ["plumbing", "electrical", "hvac", "cleaning", "repair", "glass", "other"];
var priorities = ["low", "medium", "high", "urgent"];
var statuses = ["pending", "in_progress", "completed", "cancelled"];
var maintenanceTypes = ["preventive", "reactive"];

var emptyForm = {
  title: "",
  service_type: "repair",
  property_id: "",
  unit_id: "",
  description: "",
  priority: "medium",
  status: "pending",
  maintenance_type: "reactive",
  assigned_to: "",
  supplier_id: "",
  scheduled_date: "",
  cost: "",
};

export default function Services() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var [services, setServices] = useState([]);
  var [properties, setProperties] = useState([]);
  var [units, setUnits] = useState([]);
  var [suppliers, setSuppliers] = useState([]);
  var [filterUnits, setFilterUnits] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingService, setEditingService] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [search, setSearch] = useState("");
  var [filterStatus, setFilterStatus] = useState("all");
  var [filterProperty, setFilterProperty] = useState("all");
  var [filterUnit, setFilterUnit] = useState("all");

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() { fetchData(); }, []);

  // Fetch units when form property changes (for dialog)
  useEffect(function() {
    if (form.property_id && form.property_id !== "none") {
      fetchUnits(form.property_id);
    } else {
      setUnits([]);
      setForm(function(f) { return { ...f, unit_id: "" }; });
    }
  }, [form.property_id]);

  // Fetch units when filter property changes
  useEffect(function() {
    if (filterProperty && filterProperty !== "all") {
      fetchFilterUnits(filterProperty);
    } else {
      setFilterUnits([]);
      setFilterUnit("all");
    }
  }, [filterProperty]);

  async function fetchData() {
    try {
      var servicesRes = await fetch(API_URL + "/api/services", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      var propertiesRes = await fetch(API_URL + "/api/properties", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      var suppliersRes = await fetch(API_URL + "/api/suppliers", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (servicesRes.ok) setServices(await servicesRes.json());
      if (propertiesRes.ok) setProperties(await propertiesRes.json());
      if (suppliersRes.ok) setSuppliers(await suppliersRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }

  async function fetchFilterUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setFilterUnits(await response.json());
      }
    } catch (error) {
      console.error("Error fetching filter units:", error);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var url = editingService ? API_URL + "/api/services/" + editingService.service_id : API_URL + "/api/services";
      var method = editingService ? "PUT" : "POST";

      var payload = {
        title: form.title,
        service_type: form.service_type,
        property_id: form.property_id === "none" || !form.property_id ? null : form.property_id,
        unit_id: form.unit_id === "none" || !form.unit_id ? null : form.unit_id,
        description: form.description,
        priority: form.priority,
        status: form.status,
        maintenance_type: form.maintenance_type,
        assigned_to: form.assigned_to || null,
        supplier_id: form.supplier_id === "none" || !form.supplier_id ? null : form.supplier_id,
        scheduled_date: form.scheduled_date || null,
        cost: form.cost ? parseFloat(form.cost) : null,
      };

      var response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingService ? t("common.success") : t("common.success"));
        setDialogOpen(false);
        setEditingService(null);
        setForm(emptyForm);
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(service) {
    setEditingService(service);
    setForm({
      title: service.title || "",
      service_type: service.service_type || "repair",
      property_id: service.property_id || "",
      unit_id: service.unit_id || "",
      description: service.description || "",
      priority: service.priority || "medium",
      status: service.status || "pending",
      maintenance_type: service.maintenance_type || "reactive",
      assigned_to: service.assigned_to || "",
      supplier_id: service.supplier_id || "",
      scheduled_date: service.scheduled_date || "",
      cost: service.cost?.toString() || "",
    });
    // Fetch units for the selected property
    if (service.property_id) {
      fetchUnits(service.property_id);
    }
    setDialogOpen(true);
  }

  async function handleDelete(serviceId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/services/" + serviceId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("common.success"));
        fetchData();
      } else {
        toast.error(t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function getPropertyName(propertyId) {
    if (!propertyId) return "-";
    for (var i = 0; i < properties.length; i++) {
      if (properties[i].property_id === propertyId) return properties[i].name;
    }
    return "-";
  }

  function getUnitName(unitId) {
    if (!unitId) return "-";
    for (var i = 0; i < units.length; i++) {
      if (units[i].unit_id === unitId) return units[i].name;
    }
    return unitId.substring(0, 8) + "...";
  }

  function getSupplierName(supplierId) {
    if (!supplierId) return "-";
    for (var i = 0; i < suppliers.length; i++) {
      if (suppliers[i].supplier_id === supplierId) return suppliers[i].name;
    }
    return supplierId.substring(0, 8) + "...";
  }

  function renderSupplierOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < suppliers.length; i++) {
      var s = suppliers[i];
      if (s.is_active !== false) {
        items.push(<SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>);
      }
    }
    return items;
  }

  function renderMaintenanceTypeOptions() {
    var items = [];
    for (var i = 0; i < maintenanceTypes.length; i++) {
      items.push(<SelectItem key={maintenanceTypes[i]} value={maintenanceTypes[i]}>{t("services.maintenanceTypes." + maintenanceTypes[i])}</SelectItem>);
    }
    return items;
  }

  function getPriorityBadge(priority) {
    var priorityClasses = { low: "priority-low", medium: "priority-medium", high: "priority-high", urgent: "priority-urgent" };
    return <Badge className={priorityClasses[priority] || ""}>{t("services.priorities." + priority)}</Badge>;
  }

  function getStatusBadge(status) {
    var statusClasses = {
      pending: "status-pending",
      in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      completed: "status-completed",
      cancelled: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
    };
    return <Badge className={statusClasses[status] || ""}>{t("services.statuses." + status)}</Badge>;
  }

  function getFilteredServices() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < services.length; i++) {
      var s = services[i];
      var matchesSearch = s.title && s.title.toLowerCase().indexOf(searchLower) >= 0;
      var matchesStatus = filterStatus === "all" || s.status === filterStatus;
      var matchProperty = filterProperty === "all" || s.property_id === filterProperty;
      var matchUnit = filterUnit === "all" || s.unit_id === filterUnit;
      if (matchesSearch && matchesStatus && matchProperty && matchUnit) result.push(s);
    }
    return result;
  }

  function renderFilterPropertyOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderFilterUnitOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < filterUnits.length; i++) {
      items.push(<SelectItem key={filterUnits[i].unit_id} value={filterUnits[i].unit_id}>{filterUnits[i].name}</SelectItem>);
    }
    return items;
  }

  function renderPropertyOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderUnitOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < units.length; i++) {
      items.push(<SelectItem key={units[i].unit_id} value={units[i].unit_id}>{units[i].name}</SelectItem>);
    }
    return items;
  }

  function renderServiceTypeOptions() {
    var items = [];
    for (var i = 0; i < serviceTypes.length; i++) {
      items.push(<SelectItem key={serviceTypes[i]} value={serviceTypes[i]}>{t("services.types." + serviceTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderPriorityOptions() {
    var items = [];
    for (var i = 0; i < priorities.length; i++) {
      items.push(<SelectItem key={priorities[i]} value={priorities[i]}>{t("services.priorities." + priorities[i])}</SelectItem>);
    }
    return items;
  }

  function renderStatusOptions() {
    var items = [];
    for (var i = 0; i < statuses.length; i++) {
      items.push(<SelectItem key={statuses[i]} value={statuses[i]}>{t("services.statuses." + statuses[i])}</SelectItem>);
    }
    return items;
  }

  function renderStatusFilterOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < statuses.length; i++) {
      items.push(<SelectItem key={statuses[i]} value={statuses[i]}>{t("services.statuses." + statuses[i])}</SelectItem>);
    }
    return items;
  }

  function renderServiceRows() {
    var filteredServices = getFilteredServices();
    var rows = [];
    for (var i = 0; i < filteredServices.length; i++) {
      var service = filteredServices[i];
      var serviceId = service.service_id;
      var serviceData = JSON.parse(JSON.stringify(service));
      rows.push(
        <TableRow key={serviceId} data-testid={"service-row-" + serviceId}>
          <TableCell className="font-medium">{service.title}</TableCell>
          <TableCell><Badge variant="outline">{t("services.types." + service.service_type)}</Badge></TableCell>
          <TableCell>{getPropertyName(service.property_id)}</TableCell>
          <TableCell>{service.unit_id ? <Badge variant="secondary">{getUnitName(service.unit_id)}</Badge> : "-"}</TableCell>
          <TableCell>{getPriorityBadge(service.priority)}</TableCell>
          <TableCell>{getStatusBadge(service.status)}</TableCell>
          <TableCell>{service.scheduled_date || "-"}</TableCell>
          {isAdmin && (
            <TableCell>
              {service.owner_name ? (
                <div>
                  <div className="font-medium text-sm">{service.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{service.owner_email}</div>
                </div>
              ) : "-"}
            </TableCell>
          )}
          {canEdit && (
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, serviceData)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, serviceId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    }
    return rows;
  }

  var filteredServices = getFilteredServices();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("services.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("services.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingService(null); setForm(emptyForm); setUnits([]); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-service-btn"><Plus className="w-4 h-4 mr-2" />{t("services.addService")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingService ? t("services.editService") : t("services.addNewService")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="form-label">{t("services.serviceTitle")}</Label>
                  <Input value={form.title} onChange={function(e) { setForm({ ...form, title: e.target.value }); }} placeholder="e.g., Fix leaking pipe" required data-testid="service-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("services.serviceType")}</Label>
                    <Select value={form.service_type} onValueChange={function(v) { setForm({ ...form, service_type: v }); }}>
                      <SelectTrigger data-testid="service-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderServiceTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("services.priority")}</Label>
                    <Select value={form.priority} onValueChange={function(v) { setForm({ ...form, priority: v }); }}>
                      <SelectTrigger data-testid="service-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderPriorityOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("common.status")}</Label>
                    <Select value={form.status} onValueChange={function(v) { setForm({ ...form, status: v }); }}>
                      <SelectTrigger data-testid="service-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderStatusOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("assets.property")}</Label>
                    <Select value={form.property_id || "none"} onValueChange={function(v) { setForm({ ...form, property_id: v, unit_id: "" }); }}>
                      <SelectTrigger data-testid="service-property-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                      <SelectContent>{renderPropertyOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("common.unit")}</Label>
                  <Select value={form.unit_id || "none"} onValueChange={function(v) { setForm({ ...form, unit_id: v }); }} disabled={!form.property_id || form.property_id === "none" || units.length === 0}>
                    <SelectTrigger data-testid="service-unit-select"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                    <SelectContent>{renderUnitOptions()}</SelectContent>
                  </Select>
                  {form.property_id && form.property_id !== "none" && units.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{t("common.noUnitsAvailable")}</p>
                  )}
                </div>
                <div>
                  <Label className="form-label">{t("common.description")}</Label>
                  <Textarea value={form.description} onChange={function(e) { setForm({ ...form, description: e.target.value }); }} placeholder="Detailed description..." rows={3} required data-testid="service-description-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("services.maintenanceType")}</Label>
                    <Select value={form.maintenance_type} onValueChange={function(v) { setForm({ ...form, maintenance_type: v }); }}>
                      <SelectTrigger data-testid="service-maintenance-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderMaintenanceTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("services.supplier")}</Label>
                    <Select value={form.supplier_id || "none"} onValueChange={function(v) { setForm({ ...form, supplier_id: v }); }}>
                      <SelectTrigger data-testid="service-supplier-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderSupplierOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("services.assignedTo")}</Label>
                    <Input value={form.assigned_to} onChange={function(e) { setForm({ ...form, assigned_to: e.target.value }); }} placeholder="Technician name" data-testid="service-assigned-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("services.scheduledDate")}</Label>
                    <Input type="date" value={form.scheduled_date} onChange={function(e) { setForm({ ...form, scheduled_date: e.target.value }); }} data-testid="service-scheduled-input" />
                  </div>
                </div>
                <div>
                  <Label className="form-label">{t("services.cost")}</Label>
                  <Input type="number" value={form.cost} onChange={function(e) { setForm({ ...form, cost: e.target.value }); }} placeholder="0" data-testid="service-cost-input" />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="service-submit-btn">{editingService ? t("common.update") : t("common.create")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("common.search")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="service-search-input" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="service-filter-status"><SelectValue placeholder={t("common.status")} /></SelectTrigger>
          <SelectContent>{renderStatusFilterOptions()}</SelectContent>
        </Select>
        <Select value={filterProperty} onValueChange={function(v) { setFilterProperty(v); setFilterUnit("all"); }}>
          <SelectTrigger className="w-48" data-testid="service-filter-property"><SelectValue placeholder={t("assets.property")} /></SelectTrigger>
          <SelectContent>{renderFilterPropertyOptions()}</SelectContent>
        </Select>
        <Select value={filterUnit} onValueChange={setFilterUnit} disabled={filterProperty === "all" || filterUnits.length === 0}>
          <SelectTrigger className="w-48" data-testid="service-filter-unit"><SelectValue placeholder={t("common.unit")} /></SelectTrigger>
          <SelectContent>{renderFilterUnitOptions()}</SelectContent>
        </Select>
      </div>

      {filteredServices.length > 0 ? (
        <Card className="bento-card overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("services.serviceTitle")}</TableHead>
                <TableHead>{t("common.type")}</TableHead>
                <TableHead>{t("assets.property")}</TableHead>
                <TableHead>{t("common.unit")}</TableHead>
                <TableHead>{t("services.priority")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("services.scheduledDate")}</TableHead>
                {isAdmin && <TableHead>{t("common.owner")}</TableHead>}
                {canEdit && <TableHead className="w-20">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>{renderServiceRows()}</TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("services.noServices")}</p>
            {canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("services.addFirstService")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
